// POC1
import React, { useCallback, useContext, useEffect } from 'react';
import c from 'classnames';
import { useTranslation } from 'react-i18next';
import type { BiEvent } from '@wix/da-bi/pkg/events';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import MessageModal from '@wix/da-ds/pkg/modals/dsPresets/MessageModal';
import { ModalRootProps } from '@wix/da-ds/pkg/modals/base';
import {
  DestructiveConfirmButton,
  ConfirmButton as DefaultConfirmButton,
  CancelButton,
  NeutralConfirmButton,
} from '@wix/da-ds/pkg/modals/dsPresets/MessageModal/buttons';
import { type Props as BaseButtonProps } from '@wix/da-ds/pkg/Buttons/PlainButtonOrLink';
import { yieldToMain } from '@wix/da-hooks/pkg/yieldToMain';
import { CloseOnConfirmOption } from './types';
import s from './ConfirmationModal.scss';

export interface Props extends Pick<
  ModalRootProps,
  'useTransparentOverlay' | 'onRequestClose' | 'isFullscreen'
> {
  isOpen?: boolean;
  title: string;
  text: string | React.ReactNode;
  icon?: React.ReactNode;
  variant?: 'horizontal' | 'vertical' | 'vertical-and-stacked-buttons';
  cancelBtnLabel?: string;
  cancelBtnBiData?: BiEvent;
  confirmBtnLabel: string;
  confirmBtnType?: 'default' | 'danger' | 'dark' | 'core' | 'neutral';
  confirmBtnDisabled?: boolean;
  confirmBtnBiData?: BiEvent;
  withCancelButton?: boolean;
  onCancel?(): void;
  onConfirm?(event?: React.MouseEvent | React.KeyboardEvent): void;
  closeOnConfirmation?: CloseOnConfirmOption;
  doNotShowAgainKey?: string;
  onDoNotShowAgainChange?: (key, value) => void;
  autoAcceptForDoNotShow?: boolean;
  className?: string;
}

const ConfirmButton: React.FC<
  Required<Pick<Props, 'confirmBtnType'>> & BaseButtonProps & { children: React.ReactNode }
> = ({
  confirmBtnType,
  // Destructuring children here to get around a lint issue
  children,
  ...rest
}) => {
  switch (confirmBtnType) {
    case 'danger':
      return <DestructiveConfirmButton {...rest}>{children}</DestructiveConfirmButton>;
    case 'core':
      return (
        <DefaultConfirmButton variant="core-orange" {...rest}>
          {children}
        </DefaultConfirmButton>
      );
    case 'neutral':
      return <NeutralConfirmButton {...rest}>{children}</NeutralConfirmButton>;
    case 'dark':
    case 'default':
    default:
      return <DefaultConfirmButton {...rest}>{children}</DefaultConfirmButton>;
  }
};

const ConfirmationModal: React.FC<Props> = ({
  isOpen = false,
  title,
  text,
  icon,
  variant,
  isFullscreen,
  withCancelButton = true,
  cancelBtnLabel,
  cancelBtnBiData,
  confirmBtnLabel,
  confirmBtnType = 'default',
  confirmBtnDisabled = false,
  confirmBtnBiData,
  onCancel,
  onConfirm,
  onRequestClose,
  doNotShowAgainKey,
  onDoNotShowAgainChange,
  autoAcceptForDoNotShow,
  useTransparentOverlay,
  className,
}) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);

  const doNotShowHandler = useCallback(
    value => {
      if (!doNotShowAgainKey) {
        return;
      }
      onDoNotShowAgainChange && onDoNotShowAgainChange(doNotShowAgainKey, value);
    },
    [doNotShowAgainKey, onDoNotShowAgainChange]
  );

  const handleConfirm = useCallback(
    (event: React.MouseEvent | React.KeyboardEvent) => {
      yieldToMain(() => {
        onConfirm && onConfirm(event);
      });
    },
    [onConfirm]
  );

  const handleCancel = useCallback(() => {
    yieldToMain(() => {
      onCancel && onCancel();
    });
  }, [onCancel]);

  const handleRequestClose = useCallback(
    e => {
      yieldToMain(() => {
        const cb = onRequestClose || onCancel || onConfirm;
        cb && cb(e);
      });
    },
    [onRequestClose, onCancel, onConfirm]
  );

  useEffect(() => {
    if (onConfirm && isOpen && autoAcceptForDoNotShow === true) {
      onConfirm();
    }
  }, [isOpen, autoAcceptForDoNotShow, onConfirm]);

  if (autoAcceptForDoNotShow) {
    return null;
  }

  return (
    <MessageModal
      isOpen={isOpen}
      title={title}
      message={text}
      className={c(s['root'], className)}
      contentClassName={s['content']}
      variant={variant || (isMobile ? 'vertical' : 'horizontal')}
      image={icon}
      useTransparentOverlay={useTransparentOverlay}
      footerButtons={[
        withCancelButton && (
          <CancelButton key="cancel" onClick={handleCancel} biData={cancelBtnBiData}>
            {cancelBtnLabel ?? t('popup.cancel')}
          </CancelButton>
        ),
        <ConfirmButton
          key="confirm"
          confirmBtnType={confirmBtnType}
          disabled={confirmBtnDisabled}
          onClick={handleConfirm}
          biData={confirmBtnBiData}
        >
          {confirmBtnLabel}
        </ConfirmButton>,
      ]}
      onRequestClose={handleRequestClose}
      doNotShowAgainText={t('popup.doNotShowAgain')}
      onDoNotShowAgainChange={doNotShowAgainKey ? doNotShowHandler : undefined}
      isFullscreen={isFullscreen}
    />
  );
};
ConfirmationModal.displayName = 'ConfirmationModal';

export default ConfirmationModal;
